import { Loading, Popover, Select } from '@brighterion/br-ui';
import clsx from 'clsx';
import { FastField, FieldArrayRenderProps, FieldProps } from 'formik';
import { sortBy, uniqueId } from 'lodash';
import React from 'react';
import { getFormattedParameterTypeDisplayName } from 'Utils';
import styles from './ParameterField.css';

type ParameterFieldProps = {
    arrayHelper: FieldArrayRenderProps;
    index: number;
    parameterData?: UseGetParametersResponse;
};

export const getDisplayNamesForParameterTypes = (parameterTypeHashmap: ParameterTypeHashmap = {}) => {
    const parameterTypes = Object.keys(parameterTypeHashmap);

    const displayNamesForParameterTypes = parameterTypes.map((parameterType) => {
        return {
            parameterType,
            formattedDisplayName: getFormattedParameterTypeDisplayName(parameterType, 'plural')
        };
    });

    return sortBy(displayNamesForParameterTypes, ({ formattedDisplayName }) => formattedDisplayName.toLowerCase());
};

const ParameterField = ({ arrayHelper, index, parameterData }: ParameterFieldProps) => {
    const { name, replace } = arrayHelper;

    if (parameterData === undefined) {
        return (
            <div className={styles.spinner}>
                <Loading />
            </div>
        );
    }

    const displayNamesForParameterTypes = getDisplayNamesForParameterTypes(parameterData.parameterTypeHashmap);

    return (
        <FastField name={`${name}[${index}].parameterField`}>
            {({ field, form }: FieldProps) => {
                return (
                    parameterData && (
                        <Select
                            size='sm'
                            type='search'
                            searchInputPlaceholder='Search'
                            placeholder='- Select Parameter -'
                            ellipsis
                            className={clsx(styles.parameterList, field.value.name.length < 1 && styles.unhighlight)}
                            selectedItems={field.value.name ?? []}
                            handleClickCallback={(value) => {
                                const parameter = parameterData.fieldNameHashmap[value[0]];

                                replace(index, {
                                    ...form.values.conditions[index],
                                    parameterField: {
                                        name: value,
                                        type: parameter?.type ?? ''
                                    },
                                    operatorField: [],
                                    valueField: '',
                                    valueType: ''
                                } as CustomConditionObj);

                                form.setFieldTouched(`${name}[${index}].valueField`, false);
                            }}
                            disabled={form.isSubmitting}
                            grouped
                        >
                            {displayNamesForParameterTypes.map(({ parameterType, formattedDisplayName }) => {
                                return (
                                    <optgroup label={formattedDisplayName} key={uniqueId(parameterType)}>
                                        {parameterData.original
                                            .filter((o) => {
                                                /**
                                                 * @note Remove types from Parameter list.
                                                 * The following types are NOT supported in Simple Rule.
                                                 */
                                                const notSupportedParameterListTypes = ['string[]', 'timestamp'];
                                                return (
                                                    o.parameterType === parameterType &&
                                                    !notSupportedParameterListTypes.includes(o.type.toLowerCase())
                                                );
                                            })
                                            .map((parameter) => {
                                                return (
                                                    <option
                                                        key={uniqueId(parameter.fieldName)}
                                                        value={parameter.fieldName}
                                                    >
                                                        <Popover
                                                            showOnHover
                                                            position='bottom-start'
                                                            popperClassName={styles.popOverStyle}
                                                            contentClassName={styles.popOverContentStyle}
                                                            content={
                                                                <table className={styles.popOverTable}>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td>System Name</td>
                                                                            <td>{parameter?.fieldName ?? '_'}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Display Name</td>
                                                                            <td>{parameter?.message ?? '_'}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Type</td>
                                                                            <td>{parameter?.type ?? '_'}</td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Parameter Type</td>
                                                                            <td>
                                                                                {getFormattedParameterTypeDisplayName(
                                                                                    parameter?.parameterType,
                                                                                    'singular'
                                                                                ) ?? '_'}
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td>Description</td>
                                                                            <td>{parameter?.description ?? '_'}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            }
                                                        >
                                                            <>{parameter.message}</>
                                                        </Popover>
                                                    </option>
                                                );
                                            })}
                                    </optgroup>
                                );
                            })}
                        </Select>
                    )
                );
            }}
        </FastField>
    );
};

export default React.memo(ParameterField);
